import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle, Send, Search, Tag as TagIcon, Plus, X, ShoppingBag,
  Thermometer, Link2, Lock, Copy, CheckCircle, Settings, Power, ArrowLeft, QrCode,
  Trash2, RefreshCw, PlayCircle, FileText, Pencil,
  IdCard, Mail, MapPin, Save, Cake, Braces, Paperclip, Calendar, AlertTriangle,
  Radio, Image as ImageIcon, Video, Users, Sparkles
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatMoney } from '../../utils/formatMoney';
import { formatEcuadorTime, formatEcuadorDate } from '../../utils/ecuadorTime';
import { CUSTOM_VALUES, insertAtCursor, previewCustomValues } from '../../utils/customValues';
import { ATTACHMENT_ACCEPT, buildAttachmentFromFile, blobToBase64 } from '../../utils/mediaAttachment';
import CustomAlert from './shared/CustomAlert';

// ⭐ Módulo CRM + Conversaciones — Fase 1 (planificacion-crm-conversaciones_1.md).
// UI de 3 paneles, igual que GoHighLevel: lista de conversaciones | chat
// activo | panel del contacto (pipeline, temperatura, etiquetas, notas,
// y si el contacto ya es un cliente conocido del POS, su historial de
// compras -- eso es lo que lo distingue de un CRM de WhatsApp genérico).
//
// Envío de mensajes (POST .../messages) puede devolver dos códigos de
// error distintos, y la UI los trata distinto a propósito:
//   - 501 SEND_NOT_IMPLEMENTED: solo pasa con canales provider='wazzap'
//     (su adaptador sigue sin endpoint de envío confirmado en
//     docs.wazzap.mx) -- se sabe de antemano que no va a funcionar, así
//     que se muestra un aviso fijo y persistente (banner ámbar).
//   - 502 SEND_FAILED: Wazzup24 (y cualquier proveedor con envío real)
//     intentó enviar y el proveedor lo rechazó en tiempo real -- canal
//     bloqueado, sin saldo, credenciales inválidas, etc. (ver códigos
//     documentados en backend/crm/adapters.js). Acá SÍ se muestra el
//     motivo real que manda el backend (err.response.data.error), no un
//     mensaje genérico, para que se pueda actuar sobre la causa concreta.
//   - 409 CHANNEL_DISCONNECTED: el historial puede seguir disponible, pero
//     el canal de origen fue retirado y no se puede responder desde él.

// ⭐ Preview del último mensaje en la lista de conversaciones (pedido de
// Angelo, 2026-08-07: "no muestra el tipo de archivo de un sticker") -- la
// lista nunca mostraba nada del último mensaje para NINGÚN tipo, así que un
// sticker (sin texto/caption) se veía como una tarjeta vacía sin pista de
// qué llegó. GET .../conversations ahora manda last_message_type/content
// (ver index.js), esto solo decide qué texto pintar según el tipo.
function getLastMessagePreview(type, content) {
  const labels = {
    image: '📷 Foto',
    sticker: '😄 Sticker',
    video: '🎬 Video',
    gif: '🎞️ GIF',
    audio: '🎤 Nota de voz',
    document: '📄 Documento',
    internal_notification: '⚙️ Aviso interno',
  };
  if (labels[type]) return labels[type];
  return (content || '').trim();
}

const TEMPERATURE_OPTIONS = [
  { value: 'frio', label: 'Frío', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'tibio', label: 'Tibio', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'caliente', label: 'Caliente', color: 'bg-red-100 text-red-700 border-red-300' },
];

// ⭐ Mismos swatches que usa PipelineManager.js para etapas -- consistencia
// visual entre "etapas" y "etiquetas", los dos conceptos de clasificación
// de contactos que tiene el CRM.
const TAG_COLOR_SWATCHES = ['#3b82f6', '#8b5cf6', '#f59e0b', '#f97316', '#22c55e', '#ef4444', '#ec4899', '#6b7280'];

// Historial visible de mejoras reales de este módulo. Mantenerlo junto a la
// pantalla evita prometer funciones que todavía no existen: cada entrada
// distingue lo disponible de aquello que requiere validación con WhatsApp.
const CONVERSATION_UPDATES = [
  {
    date: '4 sep 2026',
    title: 'Interfaz más cercana a WhatsApp Web',
    description: 'Nueva paleta, burbujas, buscador y navegación de Chats y Actualizaciones.',
    status: 'Disponible localmente',
  },
  {
    date: '3 sep 2026',
    title: 'Estados con texto, imagen o video',
    description: 'Preparación y validación segura para destinatarios elegidos, sin seleccionar toda la agenda automáticamente.',
    status: 'Requiere prueba con cuenta real',
  },
  {
    date: '29 ago 2026',
    title: 'Conexión directa mediante código QR',
    description: 'Reconexión de canales y consulta del estado vivo de la sesión de WhatsApp.',
    status: 'Disponible',
  },
];

// Nota: no recibe `token` por props -- apiClient ya adjunta el Authorization
// header leyendo sessionStorage (mismo patrón que Dashboard.js/MetricsPanel.js).
// Mismo patrón que DashboardPOS.js para distinguir dueño de empleado.
const isOwner = sessionStorage.getItem('userType') === 'cliente';

// ⭐ Soporte multimedia (2026-08-05, pedido de Angelo: "que se pueda ver
// audios, notas de voz, videos, imágenes... desde Conversaciones"). El
// listado de mensajes (GET .../messages) solo manda METADATA de cada
// adjunto (has_media, type, file_name, is_voice_note) -- nunca el binario,
// para no mandar varios MB de golpe al abrir una conversación (ver
// comentario en el backend, GET /api/pos/crm/conversations/:id/messages).
// Este componente pide el binario real aparte, vía GET .../messages/:id/
// media, con `apiClient` (fetch AUTENTICADO, con el mismo Authorization
// header que el resto del CRM) en vez de un <img src="URL"> directo --
// si el token fuera en la URL como query param, quedaría expuesto en logs
// del servidor y en el historial del navegador, algo que ese endpoint no
// necesita arriesgar.
//
// Imágenes/audio/stickers se cargan solas al montar (livianas, y el
// usuario casi siempre las quiere ver de una). Videos y documentos se
// cargan recién al hacer click -- pueden pesar varios MB, y descargar
// todos los que haya en un hilo largo de una sola vez sería ancho de
// banda desperdiciado que nadie pidió.
const MessageMedia = ({ msg }) => {
  const [state, setState] = useState({ url: null, loading: false, error: false });
  const objectUrlRef = useRef(null);

  const load = useCallback(async () => {
    if (state.url) return state.url;
    setState(s => ({ ...s, loading: true, error: false }));
    try {
      const res = await apiClient.get(`/api/pos/crm/messages/${msg.id}/media`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      objectUrlRef.current = url;
      setState({ url, loading: false, error: false });
      return url;
    } catch (err) {
      setState({ url: null, loading: false, error: true });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id]);

  const autoLoad = msg.type === 'image' || msg.type === 'audio' || msg.type === 'sticker' || msg.type === 'gif';

  useEffect(() => {
    if (autoLoad && msg.has_media) load();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id, msg.has_media, autoLoad]);

  if (!msg.has_media) {
    if (msg.media_too_large) return <p className="text-xs italic opacity-70 mb-1">📎 Archivo demasiado grande para mostrar acá. Revísalo desde tu teléfono.</p>;
    if (msg.media_failed) return <p className="text-xs italic opacity-70 mb-1">⚠️ No se pudo descargar este archivo.</p>;
    return null;
  }

  if (msg.type === 'image' || msg.type === 'sticker') {
    if (state.error) return <p className="text-xs italic opacity-70 mb-1">⚠️ No se pudo cargar la imagen.</p>;
    if (!state.url) return <div className="w-40 h-32 bg-black/10 animate-pulse rounded-lg mb-1" />;
    return (
      <a href={state.url} target="_blank" rel="noreferrer" className="block mb-1">
        <img src={state.url} alt={msg.type === 'sticker' ? 'Sticker' : 'Imagen'} className="max-w-[240px] max-h-[240px] rounded-lg border" />
      </a>
    );
  }

  if (msg.type === 'audio') {
    if (state.error) return <p className="text-xs italic opacity-70 mb-1">⚠️ No se pudo cargar el audio.</p>;
    if (!state.url) return <p className="text-xs italic opacity-70 mb-1">Cargando {msg.is_voice_note ? 'nota de voz' : 'audio'}...</p>;
    return (
      <div className="mb-1">
        <audio controls src={state.url} className="max-w-[240px] h-10" />
      </div>
    );
  }

  if (msg.type === 'video' || msg.type === 'gif') {
    // ⭐ 'gif' (pedido de Angelo, 2026-08-07): WhatsApp no tiene un mensaje
    // nativo de tipo GIF -- llega como video con reproducción en loop (ver
    // extractMediaInfo en baileysManager.js) -- se renderiza igual que un
    // video normal, `loop muted autoPlay` para que de verdad "se sienta"
    // como GIF (sin controles de audio que no tiene).
    if (state.url) {
      return msg.type === 'gif'
        ? <video src={state.url} loop muted autoPlay playsInline className="max-w-[240px] max-h-[240px] rounded-lg border mb-1" />
        : <video controls src={state.url} className="max-w-[240px] max-h-[240px] rounded-lg border mb-1" />;
    }
    return (
      <button onClick={load} disabled={state.loading} className="flex items-center gap-1.5 text-xs font-semibold underline mb-1 disabled:opacity-50">
        <PlayCircle className="w-3.5 h-3.5" /> {state.loading ? 'Cargando...' : (state.error ? 'Reintentar' : (msg.type === 'gif' ? 'Ver GIF' : 'Ver video'))}
      </button>
    );
  }

  if (msg.type === 'document') {
    return (
      <button
        onClick={async () => {
          const url = await load();
          if (!url) return;
          const a = document.createElement('a');
          a.href = url;
          a.download = msg.file_name || 'documento';
          document.body.appendChild(a);
          a.click();
          a.remove();
        }}
        disabled={state.loading}
        className="flex items-center gap-1.5 text-xs font-semibold underline mb-1 disabled:opacity-50"
      >
        <FileText className="w-3.5 h-3.5" /> {state.loading ? 'Descargando...' : (state.error ? 'Reintentar descarga' : (msg.file_name || 'Descargar documento'))}
      </button>
    );
  }

  return null;
};

const Conversaciones = () => {
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [channels, setChannels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contact, setContact] = useState(null);
  const [contactWorkflowRuns, setContactWorkflowRuns] = useState([]);
  const [expandedContactRunId, setExpandedContactRunId] = useState(null);
  const [contactRunSteps, setContactRunSteps] = useState({});
  const [loadingContactRun, setLoadingContactRun] = useState(null);
  // ⭐ pipelineStages sigue viviendo acá: el panel de contacto (Panel 3, más
  // abajo) todavía necesita el <select> de "Etapa" para asignar un contacto
  // sin salir del chat. El TABLERO de Pipeline (columnas, drag&drop, CRUD de
  // etapas) se mudó a su propio tab del sidebar -- ver PipelineManager.js
  // (pedido de Angelo, 2026-08-05: "que no esté dentro de conversaciones").
  const [pipelineStages, setPipelineStages] = useState([]);
  const [tags, setTags] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  // ⭐ Crear etiquetas nuevas (pedido de Angelo, 2026-08-07): el backend
  // siempre tuvo POST /api/pos/crm/tags, pero NINGÚN lugar del frontend
  // lo llamaba -- Conversaciones/Pipeline/Workflows solo podían ASIGNAR
  // etiquetas ya existentes, nunca crear la primera. Por eso "agregaba una
  // etiqueta" (en realidad una nota, el único campo libre que había) y en
  // Workflows el selector salía vacío: nunca existió ninguna etiqueta real
  // en la cuenta. Con esto se crea acá, y como Workflows/Pipeline leen el
  // mismo GET /api/pos/crm/tags, aparece en los 3 lados sin tocar nada más.
  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLOR_SWATCHES[0]);
  // ⭐ Fix (pedido de Angelo, 2026-08-07: "agregue una tag a un contacto y
  // se agrego a todos"): auditado el backend (INSERT/DELETE de
  // crm_contact_tags siempre van con contact_id + PRIMARY KEY compuesta,
  // sin bug de fondo) -- la explicación más probable es que los botones
  // punteados "+ Etiqueta" (disponibles para agregar) se veían casi
  // idénticos a las etiquetas YA asignadas, mezclados en la misma fila.
  // Ahora las asignadas son las ÚNICAS que se ven de entrada; agregar una
  // requiere abrir este selector aparte a propósito -- imposible confundir
  // "ya la tiene" con "está disponible para agregarla".
  const [showTagPicker, setShowTagPicker] = useState(false);
  // ⭐ Plantillas de mensaje (2026-08-05, "plantillas tal cual como GHL"):
  // se gestionan de verdad desde Workflows (CRUD completo), acá solo se
  // listan para insertar rápido en el composer sin salir de la
  // conversación -- mismo dato (crm_message_templates), dos puntos de uso.
  const [templates, setTemplates] = useState([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  // ⭐ Picker de valores personalizados (pedido de Angelo, 2026-08-07), ver
  // utils/customValues.js. `messageInputRef` es lo que permite insertar el
  // placeholder en la posición del cursor en vez de siempre al final.
  const [showCustomValuePicker, setShowCustomValuePicker] = useState(false);
  const messageInputRef = useRef(null);
  const [messageInput, setMessageInput] = useState('');
  // ⭐ Adjuntar imagen/audio/video/documento (pedido de Angelo, 2026-08-07):
  // `pendingAttachment` es lo que se manda junto al texto en el próximo
  // handleSendMessage -- shape { base64, mimetype, kind, fileName,
  // localPreviewUrl } (ver buildAttachmentFromFile en
  // utils/mediaAttachment.js). Un mensaje puede ser SOLO adjunto (sin
  // texto) o adjunto + caption -- ver la validación relajada en el backend
  // (POST .../messages ya no exige content si viene media).
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [attachError, setAttachError] = useState('');
  const attachInputRef = useRef(null);
  const [noteInput, setNoteInput] = useState('');
  const [search, setSearch] = useState('');
  const [customAlert, setCustomAlert] = useState(null);
  const [activeWorkspace, setActiveWorkspace] = useState('chats');
  const [statusChannelId, setStatusChannelId] = useState('');
  const [statusText, setStatusText] = useState('');
  const [statusManualRecipients, setStatusManualRecipients] = useState('');
  const [statusAudience, setStatusAudience] = useState([]);
  const [statusAudienceSearch, setStatusAudienceSearch] = useState('');
  const [statusMedia, setStatusMedia] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [statusResult, setStatusResult] = useState(null);
  const [statusSending, setStatusSending] = useState(false);
  const statusFileInputRef = useRef(null);
  // ⭐ Datos de facturación POS del contacto (pedido de Angelo, item 5:
  // "Remarketing" -> "Contactos" unificado, editable desde acá). Solo
  // aplica cuando contact.pos_customer existe -- un contacto que llegó
  // por WhatsApp y nunca compró en el POS no tiene esta ficha, y no se
  // puede crear una desde acá (eso se hace desde Caja al vender).
  const [editingPosCustomer, setEditingPosCustomer] = useState(false);
  const [posCustomerForm, setPosCustomerForm] = useState(null);
  const [savingPosCustomer, setSavingPosCustomer] = useState(false);
  // ⭐ Datos del contacto de WhatsApp (pedido de Angelo, 2026-08-05: "aún no
  // hay cómo agregar campos como nombre, cédula, número... en
  // Conversaciones manualmente"). A diferencia de la ficha POS de arriba
  // (que solo existe si el contacto ya compró), este bloque SIEMPRE está
  // disponible -- es justo el caso del screenshot que mandó Angelo: un
  // contacto que escribió por WhatsApp y nunca compró no tenía dónde
  // cargarle estos datos.
  const [editingContactInfo, setEditingContactInfo] = useState(false);
  const [contactInfoForm, setContactInfoForm] = useState(null);
  const [savingContactInfo, setSavingContactInfo] = useState(false);
  const [deletingContact, setDeletingContact] = useState(false);
  const [deletingContactBusy, setDeletingContactBusy] = useState(false);
  const [sendBlocked, setSendBlocked] = useState(false);
  const [showChannelSetup, setShowChannelSetup] = useState(false);
  // ⭐ Antes el modal SOLO se podía abrir si channels.length === 0 -- una vez
  // creado un canal (incluso uno mal configurado, como pasó con Angelo: creó
  // uno de prueba antes de tener el id correcto del canal WABA) no había
  // forma de volver a abrirlo desde la UI para corregirlo o agregar otro
  // número. 'list' muestra los canales existentes (con toggle activar/
  // desactivar); 'form' es el formulario de creación de siempre.
  // ⭐ channelView ahora tiene 3 valores: 'list' (canales existentes),
  // 'form' (crear uno nuevo), y 'qr' (solo para Baileys -- esperando que
  // se escanee el código, ver más abajo).
  const [channelView, setChannelView] = useState('list');
  const [newChannelName, setNewChannelName] = useState('');
  // ⭐ Decisión de Angelo, 2026-08-05: después de que Wazzup24 y Wazzap.mx
  // fallaran en producción (canal no encontrado, endpoint de envío
  // inexistente, error al escanear QR con 2 números distintos), se
  // abandonan ambos intermediarios y 'baileys' (self-hosted, sin
  // intermediario SaaS) pasa a ser el default. Wazzup24 se deja como
  // segunda opción por si se retoma; el tab de 'wazzap' (Wazzap.mx) se
  // quitó del todo -- dejarlo seleccionable sería mantener en la UI una
  // opción que ya se decidió no usar más, y que además nunca llegó a
  // tener un endpoint de envío confirmado (ver WazzapWhatsAppAdapter en
  // backend/crm/adapters.js, sigue siendo un stub).
  const [newChannelProvider, setNewChannelProvider] = useState('baileys');
  const [newChannelApiKey, setNewChannelApiKey] = useState('');
  const [newChannelWazzupId, setNewChannelWazzupId] = useState('');
  const [createdWebhook, setCreatedWebhook] = useState(null);
  const [webhookRegistered, setWebhookRegistered] = useState(true);
  const [webhookWarning, setWebhookWarning] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  // ⭐ Estado del flujo de conexión Baileys (QR): channelId del canal recién
  // creado con POST .../channels/baileys/start, la imagen del QR (viene
  // como data URL base64 del backend, ver GET .../channels/:id/qr), y el
  // estado de la conexión ('connecting'|'qr'|'open'|'closed'). Se hace
  // polling de ese endpoint cada 2.5s mientras channelView === 'qr' (ver
  // useEffect más abajo) -- no hay websockets todavía en este POS, mismo
  // criterio que ya se usa para refrescar conversaciones.
  const [qrChannelId, setQrChannelId] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [qrStatus, setQrStatus] = useState(null);
  const qrPollRef = useRef(null);
  const messagesEndRef = useRef(null);
  // ⭐ Fix (pedido de Angelo, 2026-08-08: "cada q se actualiza automaticamente
  // el chat me desliza para el ultimo mensaje... como el whatsapp normal, sin
  // q alla ese tiron"). El polling de abajo (cada 5s) reemplaza `messages`
  // aunque no haya nada nuevo, y antes el scroll-al-fondo corría en CADA
  // cambio de esa lista sin condición -- si el usuario estaba revisando
  // mensajes de arriba, cada ciclo lo tiraba de vuelta al fondo. Estos 3 refs
  // (no state -- no necesitan re-render) reproducen el comportamiento real
  // de WhatsApp: el mensaje nuevo se agrega a la lista siempre, pero el
  // scroll automático solo ocurre si (a) de verdad llegaron mensajes nuevos
  // -- no en cada poll -- Y (b) el usuario ya estaba cerca del fondo cuando
  // llegó. Abrir una conversación es la única excepción: ahí SIEMPRE se
  // salta al fondo (instantáneo, no 'smooth', para no ver el scroll
  // recorriendo toda la conversación vieja).
  const messagesContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const justOpenedConvRef = useRef(false);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 150;
  };

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  const loadAccess = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/access');
      setHasAccess(Boolean(res.data?.hasCrmAccess));
    } catch (err) {
      setHasAccess(false);
    } finally {
      setLoadingAccess(false);
    }
  }, []);

  const loadChannels = useCallback(async () => {
    try {
      // Evita usar una respuesta cacheada justo después de que MARKETOS
      // archive/desactive un canal desde una solicitud aprobada.
      const res = await apiClient.get('/api/pos/crm/channels', { params: { refresh: Date.now() } });
      setChannels(res.data || []);
    } catch (err) { /* silencioso: no bloquea el resto de la pantalla */ }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/conversations');
      setConversations(res.data || []);
    } catch (err) { /* silencioso, se reintenta en el próximo refresco */ }
  }, []);

  const loadPipelineAndTags = useCallback(async () => {
    try {
      const [stagesRes, tagsRes, templatesRes, customFieldsRes] = await Promise.all([
        apiClient.get('/api/pos/crm/pipeline-stages'),
        apiClient.get('/api/pos/crm/tags'),
        apiClient.get('/api/pos/crm/message-templates'),
        apiClient.get('/api/pos/crm/custom-fields'),
      ]);
      setPipelineStages(stagesRes.data || []);
      setTags(tagsRes.data || []);
      setTemplates(templatesRes.data || []);
      setCustomFields(customFieldsRes.data || []);
    } catch (err) { /* silencioso */ }
  }, []);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    if (!hasAccess) return;
    loadChannels();
    loadConversations();
    loadPipelineAndTags();
    // ⭐ Refresco por polling, mismo criterio que ya usa DashboardPOS para
    // "Ventas de hoy": no hay websockets todavía (el plan original los
    // menciona como mejora futura), así que se refresca cada 15s para que
    // lleguen mensajes nuevos sin que el usuario tenga que recargar.
    const interval = setInterval(() => {
      loadConversations();
      if (showChannelSetup) loadChannels();
    }, 15000);
    const onFocus = () => {
      loadConversations();
      if (showChannelSetup) loadChannels();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [hasAccess, loadChannels, loadConversations, loadPipelineAndTags, showChannelSetup]);

  // El selector de Estados apunta por defecto al primer WhatsApp directo
  // realmente conectado. No se elige una audiencia automáticamente.
  useEffect(() => {
    const currentStillAvailable = channels.some(channel =>
      String(channel.id) === String(statusChannelId) &&
      channel.provider === 'baileys' && channel.liveStatus === 'open'
    );
    if (currentStillAvailable) return;
    const firstConnected = channels.find(channel => channel.provider === 'baileys' && channel.liveStatus === 'open');
    setStatusChannelId(firstConnected ? String(firstConnected.id) : '');
  }, [channels, statusChannelId]);

  useEffect(() => () => {
    if (statusMedia?.localPreviewUrl) URL.revokeObjectURL(statusMedia.localPreviewUrl);
  }, [statusMedia]);

  const openConversation = async (conv) => {
    setSelectedConvId(conv.id);
    setSendBlocked(false);
    setEditingPosCustomer(false);
    setPosCustomerForm(null);
    setShowTagPicker(false);
    setCreatingTag(false);
    setContactWorkflowRuns([]);
    setExpandedContactRunId(null);
    setContactRunSteps({});
    try {
      const [msgsRes, contactRes] = await Promise.all([
        apiClient.get(`/api/pos/crm/conversations/${conv.id}/messages`),
        apiClient.get(`/api/pos/crm/contacts/${conv.contact_id}`),
      ]);
      // ⭐ Reset de los refs de scroll (ver comentario junto a su
      // declaración) -- al abrir una conversación siempre debe saltar al
      // fondo sin animación, sin importar cuántos mensajes tenía la
      // conversación anterior que estaba abierta.
      prevMessageCountRef.current = 0;
      isNearBottomRef.current = true;
      justOpenedConvRef.current = true;
      setMessages(msgsRes.data || []);
      setContact(contactRes.data || null);
      apiClient.get(`/api/pos/crm/contacts/${conv.contact_id}/workflow-runs`)
        .then(res => setContactWorkflowRuns(res.data || []))
        .catch(() => setContactWorkflowRuns([]));
      if (conv.unread_count > 0) {
        await apiClient.post(`/api/pos/crm/conversations/${conv.id}/read`);
        setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      }
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo cargar la conversación.');
    }
  };

  const toggleContactWorkflowRun = async (run) => {
    if (expandedContactRunId === run.id) {
      setExpandedContactRunId(null);
      return;
    }
    setExpandedContactRunId(run.id);
    if (contactRunSteps[run.id]) return;
    setLoadingContactRun(run.id);
    try {
      const res = await apiClient.get(`/api/pos/crm/workflow-runs/${run.id}/steps`);
      setContactRunSteps(prev => ({ ...prev, [run.id]: res.data || [] }));
    } catch (_) {
      setContactRunSteps(prev => ({ ...prev, [run.id]: [] }));
    } finally {
      setLoadingContactRun(null);
    }
  };

  useEffect(() => {
    const grew = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (!grew) return; // el poll de abajo puede repetir la misma lista -- nada que hacer

    if (justOpenedConvRef.current) {
      // Conversación recién abierta: siempre al fondo, sin animación (evita
      // ver el scroll "recorriendo" toda la conversación vieja).
      justOpenedConvRef.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      isNearBottomRef.current = true;
    } else if (isNearBottomRef.current) {
      // Llegó un mensaje nuevo de verdad (entrante por poll, o el que el
      // propio agente acaba de enviar) y el usuario ya estaba cerca del
      // fondo -- se sigue el mensaje, como WhatsApp.
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    // Si no estaba cerca del fondo, el mensaje nuevo se agrega a la lista
    // igual (ya está en `messages`), pero no se fuerza el scroll -- así no
    // se interrumpe al usuario mientras revisa mensajes de más arriba.
  }, [messages]);

  // ⭐ Fix (pedido de Angelo, 2026-08-07): el chat abierto no se
  // actualizaba solo -- había que cambiar de conversación y volver para
  // ver un mensaje nuevo. loadConversations() ya refresca la LISTA cada
  // 15s (y con eso el contador de no leídos), pero nunca refrescaba los
  // MENSAJES de la conversación que está abierta en este momento. Mismo
  // patrón de polling (no hay websockets todavía), pero cada 5s porque
  // acá sí importa que se sienta "vivo" mientras el chat está abierto.
  useEffect(() => {
    if (!selectedConvId) return;
    const poll = async () => {
      try {
        const res = await apiClient.get(`/api/pos/crm/conversations/${selectedConvId}/messages`);
        setMessages(res.data || []);
      } catch (err) { /* silencioso, se reintenta en el próximo ciclo */ }
    };
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [selectedConvId]);

  // ⭐ Inserta una plantilla en el input, con los valores personalizados ya
  // resueltos contra el contacto de la conversación abierta (ver
  // previewCustomValues en utils/customValues.js, mismo set de campos que
  // CUSTOM_VALUE_FIELDS en backend/crm/workflowEngine.js) -- acá es solo
  // una vista previa en el input: el agente todavía puede editarlo antes de
  // mandar, y el backend vuelve a renderizar por las dudas al enviar (ver
  // POST .../messages).
  // ⭐ Adjuntar imagen/audio/video/documento (pedido de Angelo, 2026-08-07):
  // si la plantilla elegida tiene un adjunto guardado (ver has_media/
  // media_kind en GET .../message-templates, TemplatesModal en
  // WorkflowsManager.js), se baja el binario real (GET .../message-
  // templates/:id/media) y se deja armado en pendingAttachment -- así el
  // agente puede insertar una plantilla CON foto/audio/video sin tener que
  // adjuntarlo a mano una segunda vez.
  const insertTemplate = async (tpl) => {
    setMessageInput(previewCustomValues(tpl.content, contact));
    setShowTemplatePicker(false);
    if (!tpl.has_media) return;
    try {
      const res = await apiClient.get(`/api/pos/crm/message-templates/${tpl.id}/media`, { responseType: 'blob' });
      const base64 = await blobToBase64(res.data);
      setPendingAttachment({
        base64,
        mimetype: tpl.media_mimetype,
        kind: tpl.media_kind,
        fileName: tpl.media_filename,
        localPreviewUrl: URL.createObjectURL(res.data),
      });
      setAttachError('');
    } catch (err) {
      setAttachError('No se pudo cargar el adjunto de la plantilla.');
    }
  };

  const handleAttachFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite elegir el mismo archivo dos veces seguidas
    if (!file) return;
    try {
      const attachment = await buildAttachmentFromFile(file);
      setPendingAttachment(attachment);
      setAttachError('');
    } catch (err) {
      setAttachError(err.message || 'No se pudo leer el archivo.');
    }
  };

  const removePendingAttachment = () => {
    if (pendingAttachment?.localPreviewUrl) URL.revokeObjectURL(pendingAttachment.localPreviewUrl);
    setPendingAttachment(null);
    setAttachError('');
  };

  const handleStatusFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const attachment = await buildAttachmentFromFile(file);
      if (!['image', 'video'].includes(attachment.kind)) {
        if (attachment.localPreviewUrl) URL.revokeObjectURL(attachment.localPreviewUrl);
        throw new Error('Los Estados solo admiten una imagen o un video.');
      }
      setStatusMedia(attachment);
      setStatusError('');
      setStatusResult(null);
    } catch (err) {
      setStatusError(err.message || 'No se pudo preparar el archivo.');
    }
  };

  const toggleStatusAudience = (phone) => {
    const normalized = String(phone || '').trim();
    if (!normalized) return;
    setStatusAudience(current => current.includes(normalized)
      ? current.filter(item => item !== normalized)
      : [...current, normalized]);
    setStatusResult(null);
  };

  const getStatusRecipients = () => {
    const manual = statusManualRecipients
      .split(/[\s,;]+/)
      .map(item => item.trim())
      .filter(Boolean);
    return [...new Set([...statusAudience, ...manual])];
  };

  const sendWhatsAppStatus = async (dryRun) => {
    const recipients = getStatusRecipients();
    if (!statusChannelId) {
      setStatusError('Conecta un canal de WhatsApp directo antes de preparar una actualización.');
      return;
    }
    if (recipients.length === 0) {
      setStatusError('Selecciona al menos un destinatario o escribe un número.');
      return;
    }
    if (!statusText.trim() && !statusMedia) {
      setStatusError('Escribe un texto o selecciona una imagen o video.');
      return;
    }

    setStatusSending(true);
    setStatusError('');
    setStatusResult(null);
    try {
      const body = {
        phones: recipients,
        text: statusText.trim(),
        caption: statusText.trim(),
        dryRun,
      };
      if (statusMedia) {
        body.media = {
          base64: statusMedia.base64,
          mimetype: statusMedia.mimetype,
          kind: statusMedia.kind,
        };
      }
      const response = await apiClient.post(`/api/pos/crm/channels/${statusChannelId}/status`, body);
      setStatusResult({
        type: dryRun ? 'validated' : 'published',
        message: dryRun
          ? `Validación correcta para ${response.data?.recipients || recipients.length} destinatario(s). Aún no se publicó nada.`
          : (response.data?.message || `Actualización enviada a ${recipients.length} destinatario(s).`),
      });
    } catch (err) {
      setStatusError(err.response?.data?.error || err.message || 'No se pudo procesar la actualización.');
    } finally {
      setStatusSending(false);
    }
  };

  const confirmStatusPublication = () => {
    const recipients = getStatusRecipients();
    if (!statusChannelId || recipients.length === 0 || (!statusText.trim() && !statusMedia)) {
      sendWhatsAppStatus(true);
      return;
    }
    setCustomAlert({
      type: 'confirm',
      title: 'Publicar actualización',
      message: `Se intentará publicar este Estado para ${recipients.length} destinatario(s) elegidos. Esta acción sí usa el WhatsApp conectado.`,
      onConfirm: () => sendWhatsAppStatus(false),
    });
  };

  const handleSendMessage = async () => {
    // ⭐ Un mensaje ahora puede ser SOLO un adjunto, sin texto (pedido de
    // Angelo, 2026-08-07) -- antes esta guarda exigía messageInput.trim()
    // siempre, así que mandar una foto sin caption era imposible.
    if ((!messageInput.trim() && !pendingAttachment) || !selectedConvId) return;
    const content = messageInput.trim();
    const attachment = pendingAttachment;
    setMessageInput('');
    setPendingAttachment(null);
    try {
      const body = { content };
      if (attachment) body.media = { base64: attachment.base64, mimetype: attachment.mimetype, kind: attachment.kind, fileName: attachment.fileName };
      await apiClient.post(`/api/pos/crm/conversations/${selectedConvId}/messages`, body);
      if (attachment?.localPreviewUrl) URL.revokeObjectURL(attachment.localPreviewUrl);
      const msgsRes = await apiClient.get(`/api/pos/crm/conversations/${selectedConvId}/messages`);
      setMessages(msgsRes.data || []);
      loadConversations();
    } catch (err) {
      if (err.response?.data?.code === 'SEND_NOT_IMPLEMENTED') {
        setSendBlocked(true);
        setMessageInput(content); // devuelve el texto al input, no se perdió
        setPendingAttachment(attachment);
      } else if (err.response?.data?.code === 'CHANNEL_DISCONNECTED') {
        showAlert('warning', 'Canal desconectado', err.response.data.error);
        setMessageInput(content);
        setPendingAttachment(attachment);
      } else if (err.response?.data?.code === 'SEND_FAILED') {
        // Wazzup24 (u otro proveedor con envío real) rechazó el mensaje en
        // tiempo real -- mostrar el motivo concreto que manda el backend,
        // no un genérico, para que se pueda actuar (canal bloqueado, sin
        // saldo, etc.)
        showAlert('error', 'No se pudo enviar', err.response.data.error || 'El proveedor rechazó el mensaje.');
        setMessageInput(content);
        setPendingAttachment(attachment);
      } else {
        showAlert('error', 'Error', 'No se pudo enviar el mensaje.');
        setMessageInput(content);
        setPendingAttachment(attachment);
      }
    }
  };

  const updateContact = async (patch) => {
    if (!contact) return;
    try {
      await apiClient.patch(`/api/pos/crm/contacts/${contact.id}`, patch);
      setContact(prev => ({ ...prev, ...patch }));
      loadConversations();
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo actualizar el contacto.');
    }
  };

  // ⭐ Ficha de facturación POS del contacto (item 5 de Angelo: "que estos
  // datos se puedan observar desde conversaciones en el modal lateral
  // derecho" + "se les pueda editar totalmente"). Reusa el mismo endpoint
  // que Contactos.js (PUT /api/pos/customers/:id) -- una sola fuente de
  // verdad para editar un pos_customer, sin importar desde qué pantalla
  // se abrió.
  const openEditPosCustomer = () => {
    if (!contact?.pos_customer) return;
    setPosCustomerForm({
      first_name: contact.pos_customer.first_name || '',
      last_name: contact.pos_customer.last_name || '',
      cedula: contact.pos_customer.cedula || '',
      phone: contact.pos_customer.phone || '',
      sector: contact.pos_customer.sector || '',
      email: contact.pos_customer.email || '',
    });
    setEditingPosCustomer(true);
  };

  const handleSavePosCustomer = async () => {
    if (!contact?.pos_customer || !posCustomerForm) return;
    if (!posCustomerForm.first_name.trim()) {
      showAlert('error', 'Falta el nombre', 'El nombre del cliente es obligatorio.');
      return;
    }
    setSavingPosCustomer(true);
    try {
      // El endpoint solo devuelve { message }, no la fila actualizada --
      // se refleja localmente con los valores que se acaban de enviar.
      await apiClient.put(`/api/pos/customers/${contact.pos_customer.id}`, posCustomerForm);
      setContact(prev => ({ ...prev, pos_customer: { ...prev.pos_customer, ...posCustomerForm } }));
      setEditingPosCustomer(false);
      setPosCustomerForm(null);
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo actualizar la ficha del cliente.');
    } finally {
      setSavingPosCustomer(false);
    }
  };

  const openEditContactInfo = () => {
    if (!contact) return;
    setContactInfoForm({
      name: contact.name || '',
      cedula: contact.cedula || '',
      // ⭐ phone_alt reemplazado por phone editable directo (pedido de
      // Angelo, 2026-08-07) + birthday/sector nuevos -- ver comentario
      // largo en PATCH /api/pos/crm/contacts/:id (backend/index.js) sobre
      // el riesgo de editar `phone` (identidad de enrutamiento).
      phone: contact.phone || '',
      birthday: contact.birthday || '',
      sector: contact.sector || '',
      email: contact.email || '',
      custom_fields: (() => { try { return typeof contact.custom_fields === 'string' ? JSON.parse(contact.custom_fields || '{}') : (contact.custom_fields || {}); } catch (_) { return {}; } })(),
    });
    setEditingContactInfo(true);
  };

  const handleSaveContactInfo = async () => {
    if (!contact || !contactInfoForm) return;
    if (!contactInfoForm.name.trim()) {
      showAlert('error', 'Falta el nombre', 'El nombre del contacto es obligatorio.');
      return;
    }
    if (!contactInfoForm.phone.trim()) {
      showAlert('error', 'Falta el teléfono', 'El teléfono del contacto es obligatorio.');
      return;
    }
    setSavingContactInfo(true);
    try {
      const patch = {
        name: contactInfoForm.name.trim(),
        cedula: contactInfoForm.cedula.trim(),
        phone: contactInfoForm.phone.trim(),
        birthday: contactInfoForm.birthday.trim(),
        sector: contactInfoForm.sector.trim(),
        email: contactInfoForm.email.trim(),
        custom_fields: contactInfoForm.custom_fields || {},
      };
      await apiClient.patch(`/api/pos/crm/contacts/${contact.id}`, patch);
      // ⭐ El backend normaliza/valida `phone` (solo dígitos/+, y rechaza si
      // ya lo usa otro contacto) -- se refleja acá tal cual lo mandamos
      // porque coincide con esa misma normalización en el caso feliz; si
      // el PATCH hubiera fallado por duplicado, el catch de abajo ni deja
      // llegar a esta línea.
      setContact(prev => ({ ...prev, ...patch }));
      loadConversations();
      setEditingContactInfo(false);
      setContactInfoForm(null);
    } catch (err) {
      // ⭐ 409 = ese número ya es de otro contacto de la cuenta (ver
      // validación en el PATCH) -- mensaje específico en vez del genérico,
      // porque acá SÍ hay algo concreto que el dueño puede corregir.
      const msg = err.response?.status === 409
        ? (err.response?.data?.error || 'Ese número ya pertenece a otro contacto.')
        : 'No se pudo actualizar el contacto.';
      showAlert('error', 'Error', msg);
    } finally {
      setSavingContactInfo(false);
    }
  };

  // ⭐ Eliminar contacto (pedido de Angelo: "en conversaciones y contactos").
  // Si el contacto también es un cliente registrado del POS
  // (pos_customer_id), esa ficha de facturación NO se toca -- solo se
  // desvincula (mismo criterio documentado en el backend). Al terminar, se
  // limpia la selección y se refresca la lista para que la conversación
  // borrada desaparezca de Panel 1.
  const handleDeleteContact = async () => {
    if (!contact) return;
    setDeletingContactBusy(true);
    try {
      await apiClient.delete(`/api/pos/crm/contacts/${contact.id}`);
      setDeletingContact(false);
      setSelectedConvId(null);
      setContact(null);
      setMessages([]);
      loadConversations();
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo eliminar el contacto.');
    } finally {
      setDeletingContactBusy(false);
    }
  };

  const addTagToContact = async (tagId) => {
    if (!contact) return;
    try {
      await apiClient.post(`/api/pos/crm/contacts/${contact.id}/tags`, { tagId });
      const tag = tags.find(t => t.id === tagId);
      if (tag) setContact(prev => ({ ...prev, tags: [...(prev.tags || []), tag] }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo asignar la etiqueta.');
    }
  };

  const removeTagFromContact = async (tagId) => {
    if (!contact) return;
    try {
      await apiClient.delete(`/api/pos/crm/contacts/${contact.id}/tags/${tagId}`);
      setContact(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t.id !== tagId) }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo quitar la etiqueta.');
    }
  };

  // ⭐ Crea la etiqueta en la cuenta (queda disponible para cualquier
  // contacto, y para el nodo Condición de Workflows) y de una vez la
  // asigna al contacto abierto -- es el flujo que Angelo esperaba que ya
  // existiera.
  const createTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    try {
      await apiClient.post('/api/pos/crm/tags', { name, color: newTagColor });
      const tagsRes = await apiClient.get('/api/pos/crm/tags');
      const freshTags = tagsRes.data || [];
      setTags(freshTags);
      const created = freshTags.find(t => t.name === name) || freshTags[freshTags.length - 1];
      setNewTagName('');
      setNewTagColor(TAG_COLOR_SWATCHES[0]);
      setCreatingTag(false);
      setShowTagPicker(false);
      if (created && contact) await addTagToContact(created.id);
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear la etiqueta.');
    }
  };

  const addNote = async () => {
    if (!noteInput.trim() || !contact) return;
    try {
      await apiClient.post('/api/pos/crm/notes', { contactId: contact.id, conversationId: selectedConvId, content: noteInput.trim() });
      setNoteInput('');
      const contactRes = await apiClient.get(`/api/pos/crm/contacts/${contact.id}`);
      setContact(contactRes.data);
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo guardar la nota.');
    }
  };

  // ⭐ Baileys no encaja en createChannel() de abajo: ese endpoint espera
  // credenciales ya conocidas (apiKey de Wazzup24), pero acá no hay nada
  // que pedir de antemano -- el "secreto" es el QR que aparece DESPUÉS de
  // crear el canal. Por eso llama a POST .../channels/baileys/start (que
  // crea el canal y arranca la conexión del lado del servidor) y pasa a
  // channelView 'qr' para empezar el polling.
  const startBaileysChannel = async () => {
    if (!newChannelName.trim()) {
      showAlert('error', 'Falta el nombre', 'Ponle un nombre a este número antes de continuar.');
      return;
    }
    try {
      const res = await apiClient.post('/api/pos/crm/channels/baileys/start', { name: newChannelName.trim() });
      setQrChannelId(res.data?.channelId || null);
      setQrImage(null);
      setQrStatus('connecting');
      setChannelView('qr');
      setNewChannelName('');
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo iniciar la conexión.');
    }
  };

  // Polling del estado/QR mientras se muestra la pantalla 'qr'. Se limpia
  // solo al salir de esa vista o desmontar el componente -- si no, queda
  // pegando al backend cada 2.5s aunque el usuario ya haya cerrado el
  // modal o vuelto a la lista.
  useEffect(() => {
    if (channelView !== 'qr' || !qrChannelId) return undefined;

    const poll = async () => {
      try {
        const res = await apiClient.get(`/api/pos/crm/channels/${qrChannelId}/qr`);
        setQrStatus(res.data?.status || null);
        setQrImage(res.data?.qr || null);
        if (res.data?.status === 'open') {
          clearInterval(qrPollRef.current);
          loadChannels();
        }
      } catch (err) { /* silencioso -- se reintenta en el próximo tick */ }
    };

    poll();
    qrPollRef.current = setInterval(poll, 2500);
    return () => clearInterval(qrPollRef.current);
  }, [channelView, qrChannelId, loadChannels]);

  const createChannel = async () => {
    if (!newChannelName.trim()) return;
    if (newChannelProvider === 'wazzup24' && (!newChannelApiKey.trim() || !newChannelWazzupId.trim())) {
      showAlert('error', 'Faltan datos', 'Para Wazzup24 necesitas el API key y el id del canal (Wazzup → Ajustes → Integración con el CRM → API).');
      return;
    }
    try {
      const res = await apiClient.post('/api/pos/crm/channels', {
        name: newChannelName.trim(),
        provider: newChannelProvider,
        ...(newChannelProvider === 'wazzup24'
          ? { apiKey: newChannelApiKey.trim(), wazzupChannelId: newChannelWazzupId.trim() }
          : {}),
      });
      setCreatedWebhook(res.data?.webhookUrl || null);
      setWebhookRegistered(res.data?.webhookRegistered !== false);
      setWebhookWarning(res.data?.webhookWarning || null);
      setNewChannelName('');
      setNewChannelApiKey('');
      setNewChannelWazzupId('');
      loadChannels();
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear el canal.');
    }
  };

  // Activar/desactivar un canal existente -- p.ej. para apagar uno mal
  // configurado (como el que Angelo creó de prueba antes de tener el id
  // correcto del canal WABA) sin perder el registro ni tener que borrar
  // nada a mano en la base de datos.
  const toggleChannelActive = async (channel) => {
    try {
      const res = await apiClient.patch(`/api/pos/crm/channels/${channel.id}`, { is_active: channel.is_active ? 0 : 1 });
      if (res.data?.approval_required) {
        return;
      }
      loadChannels();
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo actualizar el estado del canal.');
    }
  };

  // ⭐ Bug reportado 2026-08-05: si WhatsApp invalida la sesión (o el
  // proceso se reinicia sin lograr reengancharse solo), antes la única
  // salida era borrar el canal y crear uno nuevo, perdiendo el vínculo con
  // las conversaciones ya existentes. Este flujo reusa el mismo canal --
  // reabre la vista de QR y hace polling exactamente como al conectar por
  // primera vez, pero contra el canal ya existente.
  const reconnectChannel = async (channel) => {
    try {
      await apiClient.post(`/api/pos/crm/channels/${channel.id}/reconnect`);
      setQrChannelId(channel.id);
      setQrImage(null);
      setQrStatus('connecting');
      setChannelView('qr');
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo reconectar el canal.');
    }
  };

  // ⭐ Pedido de Angelo (2026-08-05): borrar un canal pasa por 2 avisos
  // encadenados -- (1) confirmación normal (Cancelar/Eliminar) y, recién
  // al confirmar esa, (2) una "confirmación de seguridad" cuyo botón
  // arranca deshabilitado con cuenta regresiva de 5s (ver prop
  // confirmDelaySeconds en CustomAlert.js) y solo se puede apretar cuando
  // llega a 0. El botón ni siquiera se renderiza para empleados (ver
  // isOwner más abajo) -- el backend además exige requireOwner, así que
  // no hace falta duplicar esa validación acá.
  //
  // El segundo setCustomAlert se difiere con setTimeout(...,0): el botón
  // "Eliminar" del primer aviso llama a este onConfirm y DESPUÉS a su
  // propio onClose (así está armado CustomAlert), así que si el segundo
  // aviso se abriera de forma síncrona acá, ese onClose posterior lo
  // cerraría de inmediato (pisando el estado en el mismo tick). Diferirlo
  // un tick evita esa carrera.
  const deleteChannel = (channel) => {
    setCustomAlert({
      type: 'confirm',
      title: 'Eliminar canal',
      message: `¿Estás seguro de eliminar el canal "${channel.name}"? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        setTimeout(() => {
          setCustomAlert({
            type: 'confirm',
            title: 'Confirmación de seguridad',
            message: `Espera 5 segundos si de verdad deseas eliminar "${channel.name}". El botón se activa solo cuando pase ese tiempo.`,
            confirmDelaySeconds: 5,
            onConfirm: async () => {
              try {
                const res = await apiClient.delete(`/api/pos/crm/channels/${channel.id}`);
                if (res.data?.approval_required) {
                  return;
                }
                loadChannels();
              } catch (err) {
                showAlert('error', 'Error', err.response?.data?.error || 'No se pudo eliminar el canal.');
              }
            }
          });
        }, 0);
      }
    });
  };

  const copyWebhook = () => {
    if (!createdWebhook) return;
    navigator.clipboard.writeText(createdWebhook).then(() => {
      setCopiedWebhook(true);
      setTimeout(() => setCopiedWebhook(false), 2000);
    });
  };

  const filteredConversations = conversations.filter(c =>
    !search.trim() ||
    (c.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.contact_phone || '').includes(search)
  );
  const selectedConversation = conversations.find(c => c.id === selectedConvId);
  const unreadTotal = conversations.reduce((total, conversation) => total + Number(conversation.unread_count || 0), 0);
  const connectedStatusChannels = channels.filter(channel => channel.provider === 'baileys' && channel.liveStatus === 'open');
  const statusContacts = conversations
    .filter(conversation => conversation.contact_phone)
    .filter((conversation, index, all) => all.findIndex(item => item.contact_phone === conversation.contact_phone) === index)
    .filter(conversation => {
      const query = statusAudienceSearch.trim().toLowerCase();
      if (!query) return true;
      return (conversation.contact_name || '').toLowerCase().includes(query) || String(conversation.contact_phone).includes(query);
    });
  const selectedChannelDisconnected = Boolean(
    selectedConversation && (!Number(selectedConversation.channel_is_active) || selectedConversation.channel_archived_at)
  );

  if (loadingAccess) {
    return <div className="p-8 text-center text-gray-500">Cargando...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Lock className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Conversaciones no está incluido en tu plan</h2>
        <p className="text-gray-500 max-w-md mb-4">
          Este módulo viene incluido desde el plan Premium, o se puede contratar aparte sobre tu plan actual. Contacta a soporte para activarlo.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#f0f2f5] text-[#111b21]">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          onConfirm={customAlert.onConfirm}
          zIndex={80}
        />
      )}

      <div className="flex items-center justify-between px-4 py-3 border-b border-[#d1d7db] bg-[#f0f2f5] gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 rounded-full bg-[#00a884] text-white flex items-center justify-center shadow-sm">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-[#111b21] leading-tight">Conversaciones</h1>
            <p className="text-xs text-[#667781] truncate">Atiende tus chats y prepara actualizaciones de WhatsApp</p>
          </div>
        </div>
        <button
          onClick={() => {
            setChannelView(channels.length === 0 ? 'form' : 'list');
            setQrChannelId(null);
            setQrImage(null);
            setQrStatus(null);
            setShowChannelSetup(true);
          }}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#00a884] text-white rounded-full text-sm font-semibold hover:bg-[#008069] shadow-sm"
        >
          {channels.length === 0 ? (<><Link2 className="w-4 h-4" /> Conectar WhatsApp</>) : (<><Settings className="w-4 h-4" /> Canales</>)}
        </button>
      </div>

      <div className="bg-white border-b border-[#e9edef] px-3 sm:px-5 flex gap-1">
        <button
          onClick={() => setActiveWorkspace('chats')}
          className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold transition ${activeWorkspace === 'chats' ? 'text-[#008069]' : 'text-[#667781] hover:bg-[#f5f6f6]'}`}
        >
          <MessageCircle className="w-4 h-4" /> Chats
          {unreadTotal > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-[#25d366] text-white text-[10px] flex items-center justify-center">{unreadTotal}</span>}
          {activeWorkspace === 'chats' && <span className="absolute inset-x-2 bottom-0 h-0.5 bg-[#00a884] rounded-full" />}
        </button>
        <button
          onClick={() => setActiveWorkspace('updates')}
          className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold transition ${activeWorkspace === 'updates' ? 'text-[#008069]' : 'text-[#667781] hover:bg-[#f5f6f6]'}`}
        >
          <Radio className="w-4 h-4" /> Actualizaciones
          {activeWorkspace === 'updates' && <span className="absolute inset-x-2 bottom-0 h-0.5 bg-[#00a884] rounded-full" />}
        </button>
      </div>

      {activeWorkspace === 'chats' && channels.length === 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
          <p><span className="font-semibold">Canal desconectado.</span> Conservamos tu historial para consulta. Conecta un nuevo WhatsApp para continuar la atención.</p>
        </div>
      )}

      {activeWorkspace === 'chats' ? (
      <div className="flex-1 flex overflow-hidden bg-white">
          {/* Panel 1: lista de conversaciones */}
          <div className="w-72 sm:w-80 flex-shrink-0 border-r border-[#d1d7db] bg-white flex flex-col">
            <div className="p-3 border-b border-[#e9edef] bg-white">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#8696a0]" />
                <input
                  type="text"
                  placeholder="Buscar contacto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border-0 rounded-lg bg-[#f0f2f5] text-sm text-[#3b4a54] placeholder:text-[#667781] focus:outline-none focus:ring-2 focus:ring-[#00a884]/30"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <p className="text-center text-gray-400 text-sm p-6">Sin conversaciones todavía.</p>
              ) : filteredConversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => openConversation(conv)}
                  // ⭐ Color de etapa (pedido de Angelo, 2026-08-07: "que el
                  // color viaje con el contacto"). Mismo criterio que la
                  // tarjeta del kanban (PipelineManager.js): borde izquierdo
                  // grueso con `stage_color` (viene del JOIN a
                  // crm_pipeline_stages en GET /crm/conversations). Gris
                  // neutro cuando el contacto no tiene etapa asignada.
                  style={{ borderLeft: `4px solid ${conv.stage_color || '#e5e7eb'}` }}
                  className={`w-full text-left px-3 py-3 border-b border-[#e9edef] hover:bg-[#f5f6f6] transition ${selectedConvId === conv.id ? 'bg-[#f0f2f5]' : 'bg-white'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-[#111b21] truncate">{conv.contact_name || conv.contact_phone || 'Sin nombre'}</span>
                    {conv.unread_count > 0 && (
                      <span className="bg-[#25d366] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{conv.unread_count}</span>
                    )}
                  </div>
                  <p className="text-xs text-[#667781] truncate">{conv.contact_phone}</p>
                  {(conv.last_message_content || conv.last_message_type) && (
                    <p className="text-xs text-[#667781] truncate mt-0.5">
                      {conv.last_message_direction === 'outbound' && <span className="text-gray-400">Tú: </span>}
                      {getLastMessagePreview(conv.last_message_type, conv.last_message_content) || '(mensaje)'}
                    </p>
                  )}
                  {/* ⭐ Origen del mensaje (pedido de Angelo: con más de 1
                      número de WhatsApp conectado, quiere ver por cuál
                      llegó cada conversación). channel_name/channel_type ya
                      venían del backend (GET .../conversations hace JOIN
                      con crm_channels) pero nunca se pintaban en el front. */}
                  {conv.channel_name && (
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">📱 {conv.channel_name}</p>
                  )}
                  {conv.last_message_at && (
                    <p className="text-xs text-gray-400 mt-0.5">{formatEcuadorDate(conv.last_message_at)} {formatEcuadorTime(conv.last_message_at)}</p>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Panel 2: chat activo */}
          <div className="flex-1 flex flex-col bg-[#efeae2] min-w-0">
            {!selectedConvId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-[#667781] text-sm px-6 text-center">
                <div className="w-20 h-20 rounded-full bg-[#d9fdd3] flex items-center justify-center mb-4"><MessageCircle className="w-9 h-9 text-[#00a884]" /></div>
                <p className="text-lg font-semibold text-[#3b4a54]">MARKETOS Conversaciones</p>
                <p className="mt-1">Selecciona un chat para comenzar a atender.</p>
              </div>
            ) : (
              <>
                {/* ⭐ Header con el canal de origen (pedido de Angelo, con
                    2+ números conectados quiere saber por cuál llegó esta
                    conversación sin tener que adivinar). */}
                {(() => {
                  return selectedConversation?.channel_name ? (
                    <div className="px-4 py-2.5 border-b border-[#d1d7db] bg-[#f0f2f5] text-xs text-[#667781] flex items-center gap-1.5">
                      📱 Vía <span className="font-semibold text-[#3b4a54]">{selectedConversation.channel_name}</span>
                    </div>
                  ) : null;
                })()}
                {selectedChannelDisconnected && (
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                    Este historial pertenece a un canal desconectado. Puedes consultarlo, pero no enviar mensajes desde ese número.
                  </div>
                )}
                <div ref={messagesContainerRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#efeae2]">
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] px-3 py-2 rounded-lg text-sm shadow-sm ${msg.internal_notification ? 'bg-amber-50 border border-amber-200 text-amber-950' : (msg.direction === 'outbound' ? 'bg-[#d9fdd3] text-[#111b21]' : 'bg-white text-[#111b21]')}`}>
                        {/* ⭐ Quién lo envió (pedido de Angelo: "que salga
                            quien envió ese mensaje"). Solo aplica a
                            salientes -- un entrante siempre es del contacto,
                            no hace falta aclararlo. sender_name viene NULL
                            cuando lo mandó el dueño (no hay fila "dueño" en
                            employees, ver el LEFT JOIN en el backend).
                            sender_type 'ai' (respuesta automática, ver
                            aiReply.js) se distingue con "🤖 IA" en vez de
                            caer en el fallback "Dueño". */}
                        {msg.internal_notification ? (
                          <>
                            <p className="text-[10px] font-semibold text-amber-800 mb-0.5">⚙️ Workflow · Aviso interno al dueño</p>
                            <p className="text-[10px] text-amber-700 mb-1">
                              Aceptado por WhatsApp
                            </p>
                          </>
                        ) : msg.direction === 'outbound' && (
                          <p className="text-[10px] font-semibold text-[#008069] mb-0.5">
                            {msg.sender_type === 'ai' ? '🤖 IA' : (msg.sender_name || 'Dueño')}
                          </p>
                        )}
                        {msg.type && msg.type !== 'text' && <MessageMedia msg={msg} />}
                        {msg.content && <p className="whitespace-pre-line">{msg.content}</p>}
                        <p className="text-[10px] mt-1 text-right text-[#667781]">{formatEcuadorTime(msg.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                {sendBlocked && (
                  <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-800">
                    El envío de mensajes desde acá todavía no está habilitado para este canal. Tu mensaje quedó en el cuadro de texto, sin enviarse.
                  </div>
                )}
                <div className="p-3 border-t border-[#d1d7db] bg-[#f0f2f5]">
                  {/* ⭐ Adjuntar imagen/audio/video/documento (pedido de
                      Angelo, 2026-08-07): preview del adjunto pendiente ANTES
                      de mandar -- imagen/video con miniatura real (via
                      localPreviewUrl, un blob: URL que nunca sale del
                      navegador), audio/documento con solo el nombre. */}
                  {pendingAttachment && (
                    <div className="mb-2 flex items-center gap-2 bg-[#d9fdd3] border border-[#b7e6ae] rounded-lg px-2 py-1.5 w-fit max-w-full">
                      {pendingAttachment.kind === 'image' && (
                        <img src={pendingAttachment.localPreviewUrl} alt="Adjunto" className="w-10 h-10 object-cover rounded" />
                      )}
                      {pendingAttachment.kind === 'video' && (
                        <video src={pendingAttachment.localPreviewUrl} className="w-10 h-10 object-cover rounded" muted />
                      )}
                      <span className="text-xs text-[#006b54] truncate max-w-[220px]">
                        📎 {pendingAttachment.kind}{pendingAttachment.fileName ? ` · ${pendingAttachment.fileName}` : ''}
                      </span>
                      <button onClick={removePendingAttachment} title="Quitar adjunto" className="text-[#008069] hover:text-[#005c4b] flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {attachError && <p className="text-[10px] text-red-500 mb-1">{attachError}</p>}
                  <div className="flex gap-2 relative">
                    {showTemplatePicker && (
                      <div className="absolute bottom-full left-3 mb-1 w-72 max-h-60 overflow-y-auto bg-white border rounded-lg shadow-lg z-10">
                        {templates.length === 0 ? (
                          <p className="text-xs text-gray-400 p-3">Todavía no tenés plantillas. Creá una desde Workflows → Plantillas.</p>
                        ) : templates.map((tpl) => (
                          <button
                            key={tpl.id}
                            onClick={() => insertTemplate(tpl)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <p className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                              {tpl.has_media && <Paperclip className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                              {tpl.name}
                            </p>
                            <p className="text-[10px] text-gray-500 truncate">{tpl.content}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setShowTemplatePicker(v => !v)}
                      title="Usar plantilla"
                      className="px-2.5 py-2 border-0 rounded-full text-[#54656f] hover:bg-white flex-shrink-0"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    {/* ⭐ Picker de valores personalizados (pedido de Angelo,
                        2026-08-07): inserta {{contact.xxx}} en la posición del
                        cursor del input (ver insertAtCursor en
                        utils/customValues.js), no siempre al final. */}
                    {showCustomValuePicker && (
                      <div className="absolute bottom-full left-12 mb-1 w-64 max-h-60 overflow-y-auto bg-white border rounded-lg shadow-lg z-10">
                        {CUSTOM_VALUES.map((cv) => (
                          <button
                            key={cv.key}
                            onClick={() => {
                              insertAtCursor(messageInputRef, messageInput, setMessageInput, `{{${cv.key}}}`);
                              setShowCustomValuePicker(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <p className="text-xs font-semibold text-gray-800">{cv.label}</p>
                            <p className="text-[10px] text-gray-400">{`{{${cv.key}}}`}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setShowCustomValuePicker(v => !v)}
                      title="Insertar valor personalizado"
                      className="px-2.5 py-2 border-0 rounded-full text-[#54656f] hover:bg-white flex-shrink-0"
                    >
                      <Braces className="w-4 h-4" />
                    </button>
                    <input ref={attachInputRef} type="file" accept={ATTACHMENT_ACCEPT} className="hidden" onChange={handleAttachFileChange} />
                    <button
                      onClick={() => attachInputRef.current?.click()}
                      title="Adjuntar imagen/audio/video"
                      className="px-2.5 py-2 border-0 rounded-full text-[#54656f] hover:bg-white flex-shrink-0"
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <input
                      ref={messageInputRef}
                      type="text"
                      placeholder="Escribe un mensaje..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                      className="flex-1 px-4 py-2.5 border-0 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00a884]/30"
                    />
                    <button onClick={handleSendMessage} className="w-10 h-10 flex items-center justify-center bg-[#00a884] text-white rounded-full hover:bg-[#008069] shadow-sm">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Panel 3: contacto */}
          {contact && (
            <div className="w-80 flex-shrink-0 border-l bg-white overflow-y-auto p-4 space-y-5">
              {/* Datos del contacto (pedido de Angelo: "agregar campos como
                  nombre, cédula, número... en Conversaciones manualmente").
                  Siempre disponible, a diferencia de la ficha POS de abajo. */}
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-gray-800">{contact.name || contact.phone || 'Sin nombre'}</h3>
                  {!editingContactInfo && (
                    <button onClick={openEditContactInfo} className="text-gray-400 hover:text-blue-600" title="Editar datos del contacto">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500">{contact.phone}</p>
                {/* ⭐ Canal de origen (pedido de Angelo, 2026-08-08): desde
                    que un mismo teléfono puede tener un contacto POR CADA
                    canal que le escribió (ver processCrmMessageQueue en el
                    backend, y el mismo dato ya visible como "📱 Vía X" en el
                    header del chat), acá también se muestra junto al resto
                    de la ficha -- es la forma de distinguir 2 contactos que
                    parecen "el mismo número" pero son 2 conversaciones
                    independientes por canales distintos. */}
                {contact.channel_name && (
                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">📱 Vía {contact.channel_name}</p>
                )}

                {editingContactInfo ? (
                  <div className="mt-2 space-y-1.5 border border-gray-200 rounded-lg p-2.5 bg-gray-50">
                    <input
                      type="text"
                      placeholder="Nombre *"
                      value={contactInfoForm.name}
                      onChange={(e) => setContactInfoForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-2 py-1.5 border rounded-lg text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Cédula"
                      value={contactInfoForm.cedula}
                      onChange={(e) => setContactInfoForm(f => ({ ...f, cedula: e.target.value }))}
                      className="w-full px-2 py-1.5 border rounded-lg text-xs"
                    />
                    {/* ⭐ Teléfono editable (pedido de Angelo, 2026-08-07,
                        reemplaza al viejo "Teléfono alterno") -- ver el
                        riesgo documentado en el PATCH del backend: cambiarlo
                        libera el número viejo (deja de enrutar mensajes
                        nuevos a este contacto) y valida que ningún otro
                        contacto de la cuenta ya lo tenga. */}
                    <input
                      type="text"
                      placeholder="Teléfono *"
                      value={contactInfoForm.phone}
                      onChange={(e) => setContactInfoForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full px-2 py-1.5 border rounded-lg text-xs"
                    />
                    <div>
                      <label className="text-[10px] text-gray-400 pl-0.5">Cumpleaños</label>
                      <input
                        type="date"
                        value={contactInfoForm.birthday}
                        onChange={(e) => setContactInfoForm(f => ({ ...f, birthday: e.target.value }))}
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Sector"
                      value={contactInfoForm.sector}
                      onChange={(e) => setContactInfoForm(f => ({ ...f, sector: e.target.value }))}
                      className="w-full px-2 py-1.5 border rounded-lg text-xs"
                    />
                    <input
                      type="email"
                      placeholder="Correo"
                      value={contactInfoForm.email}
                      onChange={(e) => setContactInfoForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-2 py-1.5 border rounded-lg text-xs"
                    />
                    {customFields.length > 0 && <div className="border-t pt-2 mt-2 space-y-2"><p className="text-[10px] font-bold text-gray-500 uppercase">Campos personalizados</p>{customFields.map(field => <div key={field.field_key}>{field.type === 'select' ? <select value={contactInfoForm.custom_fields?.[field.field_key] || ''} onChange={e => setContactInfoForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.field_key]: e.target.value } }))} className="w-full px-2 py-1.5 border rounded-lg text-xs"><option value="">{field.label}</option>{(field.options || []).map(option => <option key={option} value={option}>{option}</option>)}</select> : <input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} placeholder={field.label} value={contactInfoForm.custom_fields?.[field.field_key] || ''} onChange={e => setContactInfoForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.field_key]: e.target.value } }))} className="w-full px-2 py-1.5 border rounded-lg text-xs" />}</div>)}</div>}
                    <div className="flex gap-1.5 pt-1">
                      <button
                        onClick={() => { setEditingContactInfo(false); setContactInfoForm(null); }}
                        className="flex-1 px-2 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-100"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSaveContactInfo}
                        disabled={savingContactInfo}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Save className="w-3 h-3" /> {savingContactInfo ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  // ⭐ Fix (pedido de Angelo, 2026-08-08: "todos los datos que
                  // salen en contactos quiero que salgan allí, sin quitar los
                  // datos que ya salen allí"). Antes este bloque entero se
                  // ocultaba si TODOS los campos venían vacíos -- un contacto
                  // recién creado (solo teléfono, típico de alguien que
                  // todavía no llenó nada) no mostraba ni la posibilidad de
                  // ver esos campos, mismos que sí se ven siempre (con "—"
                  // si están vacíos) en la tabla de Contactos.js. Ahora se
                  // muestra siempre, con el mismo criterio "—" para lo que
                  // falta, más "Registrado" (fecha de alta) que Contactos.js
                  // sí trae y acá nunca se mostraba.
                  <div className="mt-1.5 space-y-0.5 text-xs text-gray-600">
                    <p className="flex items-center gap-1"><IdCard className="w-3 h-3 text-gray-400" /> {contact.cedula || <span className="text-gray-300">Sin cédula</span>}</p>
                    <p className="flex items-center gap-1"><Cake className="w-3 h-3 text-gray-400" /> {contact.birthday || <span className="text-gray-300">Sin cumpleaños</span>}</p>
                    <p className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" /> {contact.sector || <span className="text-gray-300">Sin sector</span>}</p>
                    <p className="flex items-center gap-1"><Mail className="w-3 h-3 text-gray-400" /> {contact.email || <span className="text-gray-300">Sin correo</span>}</p>
                    {contact.created_at && (
                      <p className="flex items-center gap-1 text-gray-400"><Calendar className="w-3 h-3 text-gray-400" /> Registrado el {formatEcuadorDate(contact.created_at)}</p>
                    )}
                    {customFields.length > 0 && <div className="mt-2 border-t pt-2 space-y-1"><p className="text-[10px] font-bold text-gray-400 uppercase">Campos personalizados</p>{customFields.map(field => <p key={field.field_key} className="flex justify-between gap-2"><span className="text-gray-500">{field.label}</span><span className="font-medium text-gray-700">{(() => { try { const values = typeof contact.custom_fields === 'string' ? JSON.parse(contact.custom_fields || '{}') : (contact.custom_fields || {}); return values[field.field_key] || 'Sin especificar'; } catch (_) { return 'Sin especificar'; } })()}</span></p>)}</div>}
                  </div>
                )}
              </div>

              {/* Ficha de facturación del POS -- solo existe si este contacto
                  de WhatsApp ya está vinculado a un cliente registrado en
                  Caja (contact.pos_customer_id). Si nunca compró, no hay
                  nada que editar acá; se le indica dónde sí se puede. */}
              {contactWorkflowRuns.length > 0 && (
                <div className="border border-purple-200 rounded-lg p-3 bg-purple-50/40">
                  <label className="text-xs font-bold text-purple-700 uppercase tracking-wide">Workflows del contacto</label>
                  <p className="text-[10px] text-gray-500 mt-1">Haz clic en una ejecución para ver los pasos recorridos.</p>
                  <div className="mt-2 space-y-1.5">
                    {contactWorkflowRuns.map(run => (
                      <div key={run.id} className="bg-white border border-purple-100 rounded-lg overflow-hidden">
                        <button onClick={() => toggleContactWorkflowRun(run)} className="w-full text-left px-2.5 py-2 hover:bg-purple-50">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-gray-700 truncate">{run.workflow_name || `Workflow #${run.workflow_id}`}</span>
                            <span className={`text-[10px] font-semibold ${run.status === 'completed' ? 'text-green-600' : run.status === 'error' ? 'text-red-600' : 'text-purple-600'}`}>{run.status || 'en curso'}</span>
                          </div>
                          <span className="block text-[10px] text-gray-400 mt-0.5">{run.created_at ? formatEcuadorDate(run.created_at) : 'Sin fecha'} · Ver historial</span>
                        </button>
                        {expandedContactRunId === run.id && (
                          <div className="border-t border-purple-100 px-2.5 py-2 space-y-1.5">
                            {loadingContactRun === run.id && <p className="text-[10px] text-gray-400">Cargando pasos...</p>}
                            {!loadingContactRun && (contactRunSteps[run.id] || []).length === 0 && <p className="text-[10px] text-gray-400">No hay pasos registrados para esta ejecución.</p>}
                            {(contactRunSteps[run.id] || []).map((step, index) => (
                              <div key={step.id || index} className="flex gap-2 text-[10px]">
                                <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0 font-semibold">{index + 1}</span>
                                <div className="min-w-0"><p className="font-semibold text-gray-700">{step.step_name || step.step_type || 'Paso del workflow'}</p><p className="text-gray-400">{step.status || 'ejecutado'}</p></div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contact.pos_customer ? (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <IdCard className="w-3 h-3" /> Ficha del cliente (POS)
                    </label>
                    {!editingPosCustomer && (
                      <button onClick={openEditPosCustomer} className="text-gray-400 hover:text-blue-600" title="Editar">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {editingPosCustomer ? (
                    <div className="mt-2 space-y-1.5">
                      <div className="grid grid-cols-2 gap-1.5">
                        <input
                          type="text"
                          placeholder="Nombre *"
                          value={posCustomerForm.first_name}
                          onChange={(e) => setPosCustomerForm(f => ({ ...f, first_name: e.target.value }))}
                          className="px-2 py-1.5 border rounded-lg text-xs"
                        />
                        <input
                          type="text"
                          placeholder="Apellido"
                          value={posCustomerForm.last_name}
                          onChange={(e) => setPosCustomerForm(f => ({ ...f, last_name: e.target.value }))}
                          className="px-2 py-1.5 border rounded-lg text-xs"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Cédula"
                        value={posCustomerForm.cedula}
                        onChange={(e) => setPosCustomerForm(f => ({ ...f, cedula: e.target.value }))}
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      />
                      <input
                        type="text"
                        placeholder="Teléfono"
                        value={posCustomerForm.phone}
                        onChange={(e) => setPosCustomerForm(f => ({ ...f, phone: e.target.value }))}
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      />
                      <input
                        type="text"
                        placeholder="Sector"
                        value={posCustomerForm.sector}
                        onChange={(e) => setPosCustomerForm(f => ({ ...f, sector: e.target.value }))}
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={posCustomerForm.email}
                        onChange={(e) => setPosCustomerForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      />
                      <div className="flex gap-1.5 pt-1">
                        <button
                          onClick={() => { setEditingPosCustomer(false); setPosCustomerForm(null); }}
                          className="flex-1 px-2 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-100"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleSavePosCustomer}
                          disabled={savingPosCustomer}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 disabled:opacity-50"
                        >
                          <Save className="w-3 h-3" /> {savingPosCustomer ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1.5 space-y-1 text-xs text-gray-600">
                      <p className="font-semibold text-gray-800">
                        {contact.pos_customer.first_name} {contact.pos_customer.last_name || ''}
                      </p>
                      {contact.pos_customer.cedula && (
                        <p className="flex items-center gap-1"><IdCard className="w-3 h-3 text-gray-400" /> {contact.pos_customer.cedula}</p>
                      )}
                      {contact.pos_customer.phone && (
                        <p className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-gray-400" /> {contact.pos_customer.phone}</p>
                      )}
                      {contact.pos_customer.sector && (
                        <p className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" /> {contact.pos_customer.sector}</p>
                      )}
                      {contact.pos_customer.email && (
                        <p className="flex items-center gap-1"><Mail className="w-3 h-3 text-gray-400" /> {contact.pos_customer.email}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-gray-400 italic">
                  Este contacto todavía no compró en Caja -- no tiene ficha de cliente para editar acá.
                </p>
              )}

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Etapa</label>
                {/* ⭐ Punto de color de la etapa (pedido de Angelo, 2026-08-07:
                    "que el color viaje con el contacto"). Se busca en
                    `pipelineStages` (ya cargado) en vez de `contact.stage_color`
                    porque updateContact() solo aplica el patch localmente
                    (no refetchea el contacto completo) -- así el punto se
                    actualiza al instante al cambiar de etapa, sin esperar
                    a la siguiente carga. */}
                <div className="flex items-center gap-1.5 mt-1">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-black/10"
                    style={{ backgroundColor: pipelineStages.find(s => s.id === contact.pipeline_stage_id)?.color || '#d1d5db' }}
                  />
                  <select
                    value={contact.pipeline_stage_id || ''}
                    onChange={(e) => updateContact({ pipeline_stage_id: Number(e.target.value) || null })}
                    className="flex-1 px-2 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="">Sin etapa</option>
                    {pipelineStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Thermometer className="w-3 h-3" /> Temperatura</label>
                <div className="flex gap-1.5 mt-1">
                  {TEMPERATURE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => updateContact({ temperature: opt.value })}
                      className={`flex-1 px-2 py-1 rounded-lg text-xs font-semibold border ${contact.temperature === opt.value ? opt.color : 'bg-gray-50 text-gray-400 border-gray-200'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1"><TagIcon className="w-3 h-3" /> Etiquetas</label>
                {/* Solo las YA asignadas -- chips sólidas, sin mezclar con
                    las disponibles para agregar (ver comentario en
                    showTagPicker más arriba). */}
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(contact.tags || []).length === 0 && (
                    <span className="text-xs text-gray-400 italic">Sin etiquetas</span>
                  )}
                  {(contact.tags || []).map(t => (
                    <span key={t.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: `${t.color}22`, color: t.color }}>
                      {t.name}
                      <button onClick={() => removeTagFromContact(t.id)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => { setShowTagPicker(v => !v); setCreatingTag(false); }}
                  className="flex items-center gap-1 mt-1.5 px-2 py-1 rounded-lg text-xs border border-gray-300 text-gray-600 hover:border-gray-400"
                >
                  <Plus className="w-3 h-3" /> Agregar etiqueta
                </button>

                {showTagPicker && (
                  <div className="absolute z-10 mt-1 w-56 bg-white border rounded-lg shadow-lg p-2">
                    <div className="max-h-40 overflow-y-auto space-y-0.5">
                      {tags.filter(t => !(contact.tags || []).some(ct => ct.id === t.id)).map(t => (
                        <button
                          key={t.id}
                          onClick={() => { addTagToContact(t.id); setShowTagPicker(false); }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left hover:bg-gray-50"
                        >
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                          {t.name}
                        </button>
                      ))}
                      {tags.filter(t => !(contact.tags || []).some(ct => ct.id === t.id)).length === 0 && (
                        <p className="text-[10px] text-gray-400 px-2 py-1">No hay más etiquetas para agregar.</p>
                      )}
                    </div>
                    <div className="border-t mt-1.5 pt-1.5">
                      {creatingTag ? (
                        <div className="space-y-1.5">
                          <input
                            type="text"
                            autoFocus
                            placeholder="Nombre de la etiqueta..."
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') createTag(); if (e.key === 'Escape') setCreatingTag(false); }}
                            className="w-full px-2 py-1.5 border rounded-lg text-xs"
                          />
                          <div className="flex gap-1">
                            {TAG_COLOR_SWATCHES.map(c => (
                              <button key={c} onClick={() => setNewTagColor(c)} style={{ backgroundColor: c }} className={`w-4 h-4 rounded-full ${newTagColor === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                            ))}
                          </div>
                          <div className="flex gap-1.5">
                            <button onClick={createTag} className="flex-1 text-[10px] py-1 bg-blue-600 text-white rounded font-semibold">Crear</button>
                            <button onClick={() => { setCreatingTag(false); setNewTagName(''); }} className="flex-1 text-[10px] py-1 bg-gray-200 text-gray-600 rounded font-semibold">Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setCreatingTag(true)}
                          className="w-full flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-blue-600 hover:bg-blue-50"
                        >
                          <Plus className="w-3 h-3" /> Nueva etiqueta
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {contact.purchaseHistory && contact.purchaseHistory.length > 0 && (
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Compras recientes</label>
                  <div className="mt-1 space-y-1">
                    {contact.purchaseHistory.map(sale => (
                      <div key={sale.id} className="flex justify-between text-xs text-gray-600 py-1 border-b border-gray-100">
                        <span>{formatEcuadorDate(sale.created_at)}</span>
                        <span className="font-semibold">${formatMoney(sale.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Notas internas</label>
                <div className="mt-1 space-y-2 max-h-40 overflow-y-auto">
                  {(contact.notes || []).map(n => (
                    <div key={n.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-gray-700">
                      {n.content}
                      <p className="text-[10px] text-gray-400 mt-1">{formatEcuadorDate(n.created_at)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5 mt-2">
                  <input
                    type="text"
                    placeholder="Agregar nota..."
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
                    className="flex-1 px-2 py-1.5 border rounded-lg text-xs"
                  />
                  <button onClick={addNote} className="px-2 py-1.5 bg-gray-700 text-white rounded-lg text-xs hover:bg-gray-800">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t">
                <button
                  onClick={() => setDeletingContact(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar contacto
                </button>
              </div>
            </div>
          )}
      </div>
      ) : (
        <div className="flex-1 overflow-y-auto bg-[#efeae2] p-3 sm:p-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)] gap-5">
            <section className="bg-white rounded-2xl border border-[#d1d7db] shadow-sm overflow-hidden">
              <div className="px-5 py-4 bg-[#f0f2f5] border-b border-[#d1d7db] flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-[#111b21] flex items-center gap-2">
                    <Radio className="w-5 h-5 text-[#00a884]" /> Estados de WhatsApp
                  </h2>
                  <p className="text-sm text-[#667781] mt-1">Prepara una actualización para contactos elegidos.</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 px-2.5 py-1">Experimental</span>
              </div>

              <div className="p-5 space-y-5">
                <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p><span className="font-bold">Transparencia:</span> esta función usa la sesión de WhatsApp Web conectada, no una API oficial de Meta para Estados. Puedes validarla sin publicar; el envío real debe comprobarse primero con una cuenta y un destinatario interno.</p>
                </div>

                {!isOwner && (
                  <div className="rounded-xl border border-[#d1d7db] bg-[#f7f8fa] p-3 text-sm text-[#54656f]">
                    Solo el dueño de la cuenta puede validar o publicar Estados. El equipo sí puede consultar las novedades del módulo.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#54656f] uppercase tracking-wide mb-1.5">1. Canal conectado</label>
                    <select
                      value={statusChannelId}
                      onChange={event => { setStatusChannelId(event.target.value); setStatusResult(null); }}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#d1d7db] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00a884]/30"
                    >
                      <option value="">Selecciona un WhatsApp</option>
                      {connectedStatusChannels.map(channel => <option key={channel.id} value={channel.id}>{channel.name}</option>)}
                    </select>
                    {connectedStatusChannels.length === 0 && (
                      <button
                        type="button"
                        onClick={() => { setChannelView(channels.length === 0 ? 'form' : 'list'); setShowChannelSetup(true); }}
                        className="mt-2 text-xs font-semibold text-[#008069] hover:underline"
                      >
                        Conectar o revisar WhatsApp
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#54656f] uppercase tracking-wide mb-1.5">2. Números adicionales</label>
                    <textarea
                      value={statusManualRecipients}
                      onChange={event => { setStatusManualRecipients(event.target.value); setStatusResult(null); }}
                      placeholder="Ej.: 593999999999, uno por línea"
                      rows={2}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#d1d7db] bg-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00a884]/30"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="text-xs font-bold text-[#54656f] uppercase tracking-wide flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Destinatarios de Conversaciones</label>
                    <span className="text-xs text-[#008069] font-semibold">{getStatusRecipients().length} elegido(s)</span>
                  </div>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#8696a0]" />
                    <input
                      value={statusAudienceSearch}
                      onChange={event => setStatusAudienceSearch(event.target.value)}
                      placeholder="Buscar un contacto para añadirlo..."
                      className="w-full pl-9 pr-3 py-2 rounded-xl border-0 bg-[#f0f2f5] text-sm focus:outline-none focus:ring-2 focus:ring-[#00a884]/30"
                    />
                  </div>
                  <div className="max-h-44 overflow-y-auto rounded-xl border border-[#e9edef] divide-y divide-[#e9edef]">
                    {statusContacts.length === 0 ? (
                      <p className="p-4 text-sm text-center text-[#8696a0]">No hay contactos que coincidan.</p>
                    ) : statusContacts.map(conversation => {
                      const selected = statusAudience.includes(String(conversation.contact_phone));
                      return (
                        <label key={conversation.contact_phone} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition ${selected ? 'bg-[#d9fdd3]' : 'hover:bg-[#f5f6f6]'}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleStatusAudience(conversation.contact_phone)}
                            className="w-4 h-4 accent-[#00a884]"
                          />
                          <span className="w-8 h-8 rounded-full bg-[#dfe5e7] text-[#54656f] font-bold flex items-center justify-center text-xs">
                            {(conversation.contact_name || conversation.contact_phone || '?').slice(0, 1).toUpperCase()}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold truncate">{conversation.contact_name || 'Sin nombre'}</span>
                            <span className="block text-xs text-[#667781] truncate">{conversation.contact_phone}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[#8696a0] mt-1.5">Por seguridad, nunca se selecciona toda la agenda automáticamente.</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#54656f] uppercase tracking-wide mb-1.5">3. Contenido</label>
                  <textarea
                    value={statusText}
                    maxLength={700}
                    onChange={event => { setStatusText(event.target.value); setStatusResult(null); }}
                    placeholder="Escribe el texto o pie de tu Estado..."
                    rows={4}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#d1d7db] bg-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00a884]/30"
                  />
                  <div className="flex items-center justify-between mt-1 text-[11px] text-[#8696a0]">
                    <span>Texto, una imagen o un video.</span><span>{statusText.length}/700</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <input ref={statusFileInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleStatusFileChange} />
                  <button type="button" onClick={() => statusFileInputRef.current?.click()} className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#d1d7db] text-sm font-semibold text-[#54656f] hover:bg-[#f5f6f6]">
                    {statusMedia?.kind === 'video' ? <Video className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                    {statusMedia ? 'Cambiar archivo' : 'Añadir imagen o video'}
                  </button>
                  {statusMedia && (
                    <button type="button" onClick={() => setStatusMedia(null)} className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold text-red-600 hover:bg-red-50"><X className="w-4 h-4" /> Quitar</button>
                  )}
                </div>

                {statusError && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{statusError}</div>}
                {statusResult && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"><CheckCircle className="inline w-4 h-4 mr-1.5" />{statusResult.message}</div>}

                <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-1">
                  <button
                    type="button"
                    disabled={!isOwner || statusSending}
                    onClick={() => sendWhatsAppStatus(true)}
                    className="px-4 py-2.5 rounded-full border border-[#00a884] text-[#008069] text-sm font-bold hover:bg-[#e7fce3] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {statusSending ? 'Procesando...' : 'Validar sin publicar'}
                  </button>
                  <button
                    type="button"
                    disabled={!isOwner || statusSending}
                    onClick={confirmStatusPublication}
                    className="px-4 py-2.5 rounded-full bg-[#00a884] text-white text-sm font-bold hover:bg-[#008069] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Publicar actualización
                  </button>
                </div>
              </div>
            </section>

            <div className="space-y-5">
              <section className="rounded-2xl overflow-hidden shadow-sm border border-black/10 bg-[#0b6b58] text-white">
                <div className="p-4 border-b border-white/15 flex items-center gap-2 text-sm font-bold"><Sparkles className="w-4 h-4" /> Vista previa</div>
                <div className="min-h-[380px] p-5 flex items-center justify-center bg-gradient-to-b from-[#0b6b58] to-[#06483c]">
                  <div className="w-full max-w-[250px] aspect-[9/16] rounded-2xl overflow-hidden bg-[#111b21] shadow-2xl relative flex items-center justify-center">
                    {statusMedia?.kind === 'image' && <img src={statusMedia.localPreviewUrl} alt="Vista previa del Estado" className="absolute inset-0 w-full h-full object-cover" />}
                    {statusMedia?.kind === 'video' && <video src={statusMedia.localPreviewUrl} className="absolute inset-0 w-full h-full object-cover" muted controls />}
                    {!statusMedia && <Radio className="w-16 h-16 text-white/20" />}
                    {statusText.trim() && <p className="relative z-10 m-4 rounded-xl bg-black/45 px-3 py-2 text-center text-sm whitespace-pre-line break-words">{statusText}</p>}
                  </div>
                </div>
              </section>

              <section className="bg-white rounded-2xl border border-[#d1d7db] shadow-sm p-5">
                <h2 className="font-bold text-[#111b21] mb-1">Novedades de Conversaciones</h2>
                <p className="text-xs text-[#667781] mb-4">Cambios que ya forman parte del sistema local.</p>
                <div className="space-y-4">
                  {CONVERSATION_UPDATES.map(update => (
                    <div key={`${update.date}-${update.title}`} className="relative pl-5 border-l-2 border-[#d9fdd3]">
                      <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#00a884]" />
                      <p className="text-[10px] uppercase tracking-wide font-bold text-[#8696a0]">{update.date}</p>
                      <h3 className="text-sm font-bold mt-0.5">{update.title}</h3>
                      <p className="text-xs text-[#667781] mt-1 leading-relaxed">{update.description}</p>
                      <span className={`inline-block mt-2 rounded-full px-2 py-1 text-[10px] font-bold ${update.status.includes('Requiere') ? 'bg-amber-100 text-amber-800' : 'bg-[#d9fdd3] text-[#006b54]'}`}>{update.status}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {deletingContact && (
        <CustomAlert
          type="confirm"
          title="¿Eliminar este contacto?"
          message={`Se eliminará la conversación con "${contact?.name || contact?.phone}" y su historial de WhatsApp.${contact?.pos_customer ? ' Su ficha de cliente en el POS no se toca, solo se desvincula.' : ''} Esta acción no se puede deshacer.`}
          onConfirm={handleDeleteContact}
          onClose={() => { if (!deletingContactBusy) setDeletingContact(false); }}
          confirmDelaySeconds={3}
        />
      )}

      {/* Modal: conectar canal de WhatsApp */}
      {showChannelSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                {((channelView === 'form' && channels.length > 0 && !createdWebhook) || (channelView === 'qr' && qrStatus !== 'open')) && (
                  <button onClick={() => setChannelView(channelView === 'qr' ? 'form' : 'list')} className="text-gray-400 hover:text-gray-600">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                )}
                {channelView === 'list' ? 'Canales conectados' : 'Conectar WhatsApp'}
              </h3>
              <button
                onClick={() => {
                  // ⭐ Cerrar el modal en plena vista 'qr' no debe dejar el
                  // polling corriendo en segundo plano (ver useEffect de
                  // startBaileysChannel más arriba) -- por eso se limpia
                  // qrChannelId acá, no solo se oculta el modal.
                  setShowChannelSetup(false);
                  setCreatedWebhook(null);
                  setChannelView('list');
                  setQrChannelId(null);
                  setQrImage(null);
                  setQrStatus(null);
                }}
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {channelView === 'list' ? (
              <>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-500">Esta lista se actualiza cuando MARKETOS aplica un cambio aprobado.</p>
                  <button
                    type="button"
                    onClick={loadChannels}
                    className="shrink-0 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    Actualizar
                  </button>
                </div>
                {channels.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-4">Todavía no tienes ningún canal conectado.</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {channels.map((c) => {
                      // Para canales Baileys, el estado que importa es el
                      // socket en vivo (liveStatus), no is_active de la DB
                      // -- pueden estar desincronizados si WhatsApp invalidó
                      // la sesión o el proceso se reinició sin reengancharse
                      // solo (ver GET /api/pos/crm/channels en el backend).
                      const isBaileys = c.provider === 'baileys';
                      const isLiveOpen = isBaileys ? c.liveStatus === 'open' : c.is_active;
                      const needsReconnect = isBaileys && c.liveStatus !== 'open' && c.liveStatus !== 'connecting';

                      return (
                        <div key={c.id} className="flex items-center justify-between border rounded-lg px-3 py-2 gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                            <p className="text-xs text-gray-500">{c.provider === 'wazzup24' ? 'Wazzup24' : c.provider === 'baileys' ? 'WhatsApp directo' : 'Wazzap'}</p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {needsReconnect ? (
                              <button
                                onClick={() => reconnectChannel(c)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200"
                              >
                                <RefreshCw className="w-3.5 h-3.5" /> Desconectado · Reconectar
                              </button>
                            ) : isBaileys && c.liveStatus === 'connecting' ? (
                              <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-500">
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Conectando...
                              </span>
                            ) : (
                              <button
                                onClick={() => toggleChannelActive(c)}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${isLiveOpen ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                              >
                                <Power className="w-3.5 h-3.5" /> {isLiveOpen ? 'Activo' : 'Inactivo'}
                              </button>
                            )}
                            {isOwner && (
                              <button
                                onClick={() => deleteChannel(c)}
                                title="Eliminar canal"
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={() => setChannelView('form')}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Agregar canal
                </button>
              </>
            ) : channelView === 'qr' ? (
              <div className="text-center">
                {qrStatus === 'open' ? (
                  <>
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-gray-800 mb-1">¡Conectado!</p>
                    <p className="text-xs text-gray-500 mb-4">Tu WhatsApp ya está vinculado. Ya puedes recibir y enviar mensajes desde acá.</p>
                    <button
                      onClick={() => { setChannelView('list'); setQrChannelId(null); setQrImage(null); setQrStatus(null); }}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                    >
                      Listo
                    </button>
                  </>
                ) : qrImage ? (
                  <>
                    <p className="text-sm text-gray-600 mb-3">Abre WhatsApp en tu teléfono → Dispositivos vinculados → Vincular un dispositivo, y escanea este código:</p>
                    <img src={qrImage} alt="Código QR para vincular WhatsApp" className="w-56 h-56 mx-auto border rounded-lg" />
                    <p className="text-xs text-gray-400 mt-3">No cierres esta ventana mientras escaneas.</p>
                  </>
                ) : qrStatus === 'closed' ? (
                  <>
                    <p className="text-sm text-red-600 mb-4">No se pudo conectar. El número puede estar vinculado en otro lugar, o WhatsApp cerró la conexión.</p>
                    <button
                      onClick={() => { setChannelView('form'); setQrChannelId(null); setQrImage(null); setQrStatus(null); }}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                    >
                      Reintentar
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 py-10">Generando código QR...</p>
                )}
              </div>
            ) : !createdWebhook ? (
              <>
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setNewChannelProvider('baileys')}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1 ${newChannelProvider === 'baileys' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    <QrCode className="w-3.5 h-3.5" /> WhatsApp (QR)
                  </button>
                  <button
                    onClick={() => setNewChannelProvider('wazzup24')}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-semibold border ${newChannelProvider === 'wazzup24' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Wazzup24
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-3">Ponle un nombre a este número (ej. "WhatsApp Ventas") para identificarlo.</p>
                <input
                  type="text"
                  placeholder="Nombre del canal"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
                />

                {newChannelProvider === 'wazzup24' && (
                  <>
                    <p className="text-xs text-gray-500 mb-2">
                      En tu cuenta de Wazzup: Ajustes → Integración con el CRM → API → Add, copia el API key. El id del canal lo ves en Canales, al abrir el número ya conectado por QR.
                    </p>
                    <input
                      type="text"
                      placeholder="API key de Wazzup24"
                      value={newChannelApiKey}
                      onChange={(e) => setNewChannelApiKey(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
                    />
                    <input
                      type="text"
                      placeholder="Id del canal (channelId, uuid)"
                      value={newChannelWazzupId}
                      onChange={(e) => setNewChannelWazzupId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
                    />
                  </>
                )}

                {newChannelProvider === 'baileys' && (
                  <p className="text-xs text-gray-500 mb-3">
                    Conexión directa por QR, sin intermediarios -- igual que vincular WhatsApp Web desde tu teléfono. Al crear el canal te va a aparecer el código para escanear.
                  </p>
                )}

                <button
                  onClick={newChannelProvider === 'baileys' ? startBaileysChannel : createChannel}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                >
                  {newChannelProvider === 'baileys' ? 'Generar código QR' : 'Crear canal'}
                </button>
              </>
            ) : (
              <>
                {newChannelProvider === 'wazzup24' ? (
                  webhookRegistered ? (
                    <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-3 text-sm flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Canal creado y webhook registrado automáticamente en tu cuenta de Wazzup24. Ya puedes recibir mensajes acá, no necesitas hacer nada más en Wazzup.</span>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 mb-3 text-sm">
                      <p className="font-semibold mb-1">Canal creado, pero el webhook no se pudo registrar automáticamente.</p>
                      {webhookWarning && <p className="mb-2">Motivo: {webhookWarning}</p>}
                      <p>Wazzup24 no tiene un campo en su panel para pegar esta URL a mano: hay que reintentar el registro por API. Avísale a soporte para reintentarlo.</p>
                    </div>
                  )
                ) : (
                  <p className="text-sm text-gray-600 mb-2">
                    Pega esta URL en la configuración de webhook de tu número, dentro del panel de Wazzap:
                  </p>
                )}
                {(newChannelProvider !== 'wazzup24' || !webhookRegistered) && (
                  <div className="flex gap-2 mb-3">
                    <input readOnly value={createdWebhook} className="flex-1 px-2 py-2 border rounded-lg text-xs bg-gray-50 text-gray-600" />
                    <button onClick={copyWebhook} className="px-3 bg-gray-700 text-white rounded-lg">
                      {copiedWebhook ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => { setCreatedWebhook(null); setWebhookRegistered(true); setWebhookWarning(null); setChannelView('list'); }}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                >
                  Listo
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversaciones;
